import { CommonModule, TitleCasePipe } from "@angular/common";
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  DxLoadPanelModule,
  DxSwitchModule,
  DxTabPanelModule,
  DxToolbarModule,
  DxAutocompleteModule,
  DxAutocompleteComponent,
  DxDataGridModule,
  DxDataGridComponent,
  DxDateBoxModule,
  DxCheckBoxModule
} from "devextreme-angular";
import { locale } from "devextreme/localization";
import { RealCostData, RealCostRecord, RealCostMultiplier, ResourceCostOverride } from "../../../domain/models/real-cost.model";
import { RealCostService } from "./services/real-cost.service";
import { lastValueFrom, forkJoin } from "rxjs";
import { GlobalSearchService } from "../../../core/components/global-search/global-search.service";
import {
  GlobalSearchCategory,
  GlobalSearchRecord,
  GlobalSearchResponse,
  GroupedRecord,
} from "../../../domain/models/global-search.model";
import { showNotification } from "../../../core/utils/toast";
import { EnterKeyEvent, SelectionChangedEvent } from "devextreme/ui/autocomplete";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import { LoadOptions } from "devextreme/data";
import { SignalService } from "../../../core/services/loader.service";
import { ActionButtonComponent } from "../../shared/action-button/action-button.component";
import { PowerBIEmbedModule, PowerBIReportEmbedComponent } from "powerbi-client-angular";
import { IReportEmbedConfiguration, models } from "powerbi-client";
import { AuthService } from "../../../core/services/auth.service";
import { ReportService } from "../report/report.service";
import { Router } from "@angular/router";
import { CostOverridePopupComponent } from "./components/cost-override-popup/cost-override-popup.component";

@Component({
  selector: "app-real-cost",
  standalone: true,
  imports: [
    CommonModule,
    DxLoadPanelModule,
    DxSwitchModule,
    DxTabPanelModule,
    DxToolbarModule,
    DxAutocompleteModule,
    DxDataGridModule,
    DxDateBoxModule,
    ActionButtonComponent,
    PowerBIEmbedModule,
    DxCheckBoxModule,
    CostOverridePopupComponent,
],
  providers: [TitleCasePipe],
  templateUrl: "./real-cost.component.html",
  styleUrl: "./real-cost.component.scss",
})
export class RealCostComponent implements OnInit {

  @ViewChild('multipliersGrid', { static: false })
  multipliersGrid!: DxDataGridComponent;

  @ViewChild(PowerBIReportEmbedComponent)
  reportObj!: PowerBIReportEmbedComponent;

  searchQuery: string = "";
  gridDataSource: RealCostRecord[] = [];
  gridMultipliers: any[] = []; // Changed to any[] to support hierarchical structure with custom properties
  reportConfig!: models.IPaginatedReportLoadConfiguration;
  contracts = new DataSource({
    store: new CustomStore({
      key: "id",
      load: (loadOptions: LoadOptions) => {
        this.searchQuery = loadOptions.searchValue;
        return lastValueFrom(
          this.globalService.retrieveGlobalData(this.searchQuery),
        )
          .then((response: GlobalSearchResponse) => {
            const groupedData = this.mapSearchToGroupData(response);
            if (response.search.length === 0 || groupedData.length === 0) {
              showNotification("No results found for this search", "warning");
            }
            return {
              data: groupedData,
            };
          })
          .catch(() => {
            if (loadOptions.searchValue.length < 3) {
              showNotification(
                "Search query must be at least 3 characters long",
                "warning",
              );
            } else {
              showNotification("Error while loading data", "error");
            }
            throw new Error("Data loading error");
          });
      },
    }),
  });

  contractId: number;
  contractCode: string = '';
  invoicePreviewCode: string | null = null;
  invoicePreviewCreatedAt: string | null = null;
  straight40: boolean = false;
  hasPageAccess: boolean = true;

  // Role-based visibility flags
  hasRealCostRole: boolean = false;
  hasS40Role: boolean = false;

  /** Admin (SuperUser) will have both flags = true from the backend */
  get canSeeApplyAndStraight40(): boolean {
      return this.hasS40Role || this.hasRealCostRole;
  }

  get canSeeMultipliersGrid(): boolean {
    return this.hasRealCostRole;
  }

  get canSeeCostOverride(): boolean {
    return this.hasRealCostRole;
  }

  get canSeeResultGrid(): boolean {
    return this.hasRealCostRole;
  }

  get canSeeShowReport(): boolean {
    return this.hasRealCostRole;
  }

  private formatLocalDate(dateStr: string | null): string | null {
    if (!dateStr) return null;
    return new Intl.DateTimeFormat(navigator.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  }
  isReportVisible: boolean = false;
  isCostOverridePopupVisible: boolean = false;
  costOverrideResources: ResourceCostOverride[] = [];
  isSavingCostOverrides: boolean = false;

  filters = {
    startDate: new Date(),
    endDate: new Date(),
    contractId: null as number | null,
  };

  constructor(
    private realCostService: RealCostService,
    private globalService: GlobalSearchService,
    private loaderService: SignalService,
    private authService: AuthService,
    private reportService: ReportService,
    private router: Router,
  ) {
    this.contractId = 0;
  }

  ngOnInit(): void {
    locale("it-IT");
    this.loaderService.loadingSignal.set(false);

    forkJoin([
      this.realCostService.checkPageAccess('realCost'),
      this.realCostService.checkPageAccess('S40'),
    ]).subscribe({
      next: ([hasRealCost, hasS40]) => {
        this.hasRealCostRole = hasRealCost;
        this.hasS40Role = hasS40;
        this.hasPageAccess = hasRealCost || hasS40;
      },
      error: () => { this.hasPageAccess = false; },
    });
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    if (event.selectedItem) {
      this.contractId = event.selectedItem.id;
    }
  }

  mapSearchToGroupData(response: GlobalSearchResponse): GroupedRecord[] {
    return response.search.filter((x) => x.type == 'Contract').map((category: GlobalSearchCategory) => ({
      key: category.type,
      items: category.count > 0 ? category.records : null,
      count: category.count,
    }));
  }

  onKeyDown(e: EnterKeyEvent, autoComplComponent: DxAutocompleteComponent) {
    autoComplComponent.instance
      .getDataSource()
      .reload()
      .then((res) => autoComplComponent.instance.open());
  }

  redirectToSelectedItem(
    item: GlobalSearchRecord,
    element: DxAutocompleteComponent,
  ) {
    if (!this.hasPageAccess) {
      showNotification("You don't have permission to access this page", "warning");
      return;
    }
    this.contractId = item.realId!;
    this.contractCode = item.code!;
    this.loaderService.loadingSignal.set(true);
    lastValueFrom(
      this.realCostService.loadRealCostData(item.code!),
    )
      .then((response: RealCostData) => {
        this.contractId = response.contractId;
        this.gridMultipliers = this.transformToHierarchicalStructure(response.multipliers);
        this.gridDataSource = response.records;
        this.invoicePreviewCode = response.invoicePreviewCode ?? null;
        this.invoicePreviewCreatedAt = this.formatLocalDate(response.invoicePreviewCreatedAt ?? null);
        this.straight40 = response.straight40 ?? false;
        
        // Automatically calculate Table 2 values after loading
        this.recalculateTable2();
      })
      .catch(() => {
        showNotification("Error while loading data", "error");
        throw new Error("Data loading error");
      })
      .finally(() => {
        this.loaderService.loadingSignal.set(false);
      });
  }

  /**
   * Checks if a row has any multiplier values set
   */
  hasMultiplierValues(row: any): boolean {
    return row.mult1 !== null || row.mult2 !== null || row.mult3 !== null;
  }

  /**
   * Handles multiplier cell edits - rounds values to 4 decimal places
   */
  onMultiplierEdit(event: any): void {
    const data = event.data;
    
    // Round multiplier values to 4 decimal places and enforce minimum value
    if (data.mult1 !== null && data.mult1 !== undefined) {
      data.mult1 = Math.round(data.mult1 * 10000) / 10000;
      if (data.mult1 < 0.0001) {
        data.mult1 = 0.0001;
        showNotification("Minimum value for multipliers is 0.0001", "warning");
      }
    }
    if (data.mult2 !== null && data.mult2 !== undefined) {
      data.mult2 = Math.round(data.mult2 * 10000) / 10000;
      if (data.mult2 < 0.0001) {
        data.mult2 = 0.0001;
        showNotification("Minimum value for multipliers is 0.0001", "warning");
      }
    }
    if (data.mult3 !== null && data.mult3 !== undefined) {
      data.mult3 = Math.round(data.mult3 * 10000) / 10000;
      if (data.mult3 < 0.0001) {
        data.mult3 = 0.0001;
        showNotification("Minimum value for multipliers is 0.0001", "warning");
      }
    }
    
    // Trigger grid refresh to show updated values
    this.gridMultipliers = [...this.gridMultipliers];
  }

  /**
   * Clears multipliers in the current row (Red X functionality)
   */
  onClearMultipliers(row: any): void {
    row.mult1 = null;
    row.mult2 = null;
    row.mult3 = null;
    
    // Trigger grid refresh
    this.gridMultipliers = [...this.gridMultipliers];
    
    showNotification("Multipliers cleared", "success");
  }

  /**
   * Clears multipliers in all child rows (Green Arrow Down functionality)
   * - For Contract row: clears all milestone and task rows
   * - For Milestone row: clears all task rows under that milestone
   */
  onClearChildren(row: any): void {
    if (row.isContractHeader) {
      // Clear all milestone and task rows (everything except the contract row)
      this.gridMultipliers.forEach(r => {
        if (!r.isContractHeader) {
          r.mult1 = null;
          r.mult2 = null;
          r.mult3 = null;
        }
      });
      showNotification("All child rows cleared", "success");
    } else if (row.isMilestoneHeader) {
      // Clear all task rows under this milestone
      const milestoneName = row.milestone;
      let clearStarted = false;
      
      for (let i = 0; i < this.gridMultipliers.length; i++) {
        const r = this.gridMultipliers[i];
        
        // Found the milestone header row
        if (r.isMilestoneHeader && r.milestone === milestoneName) {
          clearStarted = true;
          continue;
        }
        
        // Stop when we hit another milestone or contract header
        if (clearStarted && (r.isMilestoneHeader || r.isContractHeader)) {
          break;
        }
        
        // Clear task rows under this milestone
        if (clearStarted && r.isTaskRow) {
          r.mult1 = null;
          r.mult2 = null;
          r.mult3 = null;
        }
      }
      showNotification("Task rows cleared", "success");
    }
    
    // Trigger grid refresh
    this.gridMultipliers = [...this.gridMultipliers];
  }

  /**
   * APPLY button handler - Saves multipliers to backend and recalculates Table 2
   */
  onApply(): void {
    if (!this.contractId) {
      showNotification("Please select a contract first", "warning");
      return;
    }

    // Force grid to commit any pending edits before saving
    if (this.multipliersGrid && this.multipliersGrid.instance) {
      this.multipliersGrid.instance.saveEditData();
    }

    // S40-only users don't have multipliers grid visible, send null
    const multipliersToSave = this.canSeeMultipliersGrid
      ? this.extractMultipliersFromGrid()
      : null;

    this.loaderService.loadingSignal.set(true);
    lastValueFrom(this.realCostService.saveMultipliers(this.contractId, multipliersToSave, this.straight40))
      .then(() => {
        if (this.canSeeResultGrid) {
          this.recalculateTable2();
        }
        this.isReportVisible = false;
        showNotification("Settings saved successfully", "success");
      })
      .catch(() => {
        showNotification("Error while saving", "error");
      })
      .finally(() => {
        this.loaderService.loadingSignal.set(false);
      });
  }

  /**
   * Extracts the multiplier values from the hierarchical grid back into RealCostMultiplier[]
   */
  private extractMultipliersFromGrid(): RealCostMultiplier[] {
    return this.gridMultipliers.map(row => ({
      contractId: row._contractId ?? this.contractId,
      subprojectCode: row._subprojectCode ?? null,
      tsTaskId: row._tsTaskId ?? null,
      contract: row.contract ?? '',
      milestone: row.milestone ?? '',
      task: row.task ?? null,
      mult1: row.mult1,
      mult2: row.mult2,
      mult3: row.mult3
    }));
  }

  /**
   * Recalculates Table 2 (Final Revenue and Diff) using hierarchical multiplier logic
   */
  private recalculateTable2(): void {
    this.gridDataSource.forEach(task => {
      // Find multipliers using hierarchical fallback
      const multipliers = this.findMultipliersForTask(task.milestone, task.task);
      
      // Calculate Final Revenue = Real Cost × mult1 × mult2 × mult3 (null = 1)
      const mult1 = multipliers.mult1 ?? 1;
      const mult2 = multipliers.mult2 ?? 1;
      const mult3 = multipliers.mult3 ?? 1;
      
      task.finalRevenue = Math.round(task.realCost * mult1 * mult2 * mult3 * 100) / 100;
      
      // Calculate Diff = Final Revenue - CLM Revenue
      task.diff = Math.round((task.finalRevenue - task.clmRevenue) * 100) / 100;
    });
    
    // Trigger grid refresh
    this.gridDataSource = [...this.gridDataSource];
  }

  /**
   * PayCor button handler - Placeholder for future implementation
   */
  onPayCor(): void {
    this.router.navigate(["/pay-cor"]);
  }

  onOpenCostOverride(): void {
    if (!this.contractId) {
      showNotification("Please select a contract first", "warning");
      return;
    }

    this.loaderService.loadingSignal.set(true);
    lastValueFrom(this.realCostService.loadCostOverrides(this.contractId))
      .then((resources) => {
        this.costOverrideResources = resources;
        this.isCostOverridePopupVisible = true;
      })
      .catch(() => {
        showNotification("Error loading resource costs", "error");
      })
      .finally(() => {
        this.loaderService.loadingSignal.set(false);
      });
  }

  onSaveCostOverrides(overrides: ResourceCostOverride[]): void {
    this.isSavingCostOverrides = true;
    this.loaderService.loadingSignal.set(true);
    lastValueFrom(this.realCostService.saveCostOverrides(this.contractId, overrides))
      .then(() => {
        showNotification("Cost overrides saved", "success");
        this.isCostOverridePopupVisible = false;
        return lastValueFrom(this.realCostService.loadRealCostData(this.contractCode));
      })
      .then((response: RealCostData) => {
        this.gridMultipliers = this.transformToHierarchicalStructure(response.multipliers);
        this.gridDataSource = response.records;
        this.invoicePreviewCode = response.invoicePreviewCode ?? null;
        this.invoicePreviewCreatedAt = this.formatLocalDate(response.invoicePreviewCreatedAt ?? null);
        this.straight40 = response.straight40 ?? false;
        this.recalculateTable2();
      })
      .catch(() => {
        showNotification("Error saving cost overrides", "error");
      })
      .finally(() => {
        this.isSavingCostOverrides = false;
        this.loaderService.loadingSignal.set(false);
      });
  }

  /**
   * Show Report button handler - Loads and displays Power BI paginated report
   */
  /**
   * Show Report button handler - Loads and displays Power BI paginated report using SDK
   */
  async onShowReport(): Promise<void> {
    if (!this.contractId) {
      showNotification("Please select a contract first", "warning");
      return;
    }

    try {
      this.loaderService.loadingSignal.set(true);
      
      // Get access token using Master User authentication
      const accessToken = await this.authService.getTokenForPowerBiReports();
      
      if (accessToken) {
        // Get report configuration from service
        this.reportConfig = this.reportService.setupRealCostReportConfiguration(
          this.contractId,
          accessToken
        );
        this.isReportVisible = true;
        showNotification("Report loaded successfully", "success");
      }
    } catch (error) {
      console.error("Error loading Power BI report:", error);
      showNotification("Error while loading Power BI report", "error");
      this.isReportVisible = false;
    } finally {
      this.loaderService.loadingSignal.set(false);
    }
  }

  /**
   * Finds the appropriate multipliers for a task using hierarchical fallback:
   * 1. Check task row - use if at least 1 multiplier is not null
   * 2. Else check milestone row - use if at least 1 multiplier is not null
   * 3. Else use contract row
   * 4. If all levels have null multipliers, returns null (treated as 1 in calculations)
   */
  private findMultipliersForTask(milestoneName: string, taskName: string): { mult1: number | null, mult2: number | null, mult3: number | null } {
    // Find task row in Table 1
    const taskRow = this.gridMultipliers.find(r => 
      r.isTaskRow && r._milestoneName === milestoneName && r.task === taskName
    );
    
    // Check if task row has at least one multiplier value
    if (taskRow && this.hasMultiplierValues(taskRow)) {
      return { mult1: taskRow.mult1, mult2: taskRow.mult2, mult3: taskRow.mult3 };
    }
    
    // Find milestone row in Table 1
    const milestoneRow = this.gridMultipliers.find(r => 
      r.isMilestoneHeader && r.milestone === milestoneName
    );
    
    // Check if milestone row has at least one multiplier value
    if (milestoneRow && this.hasMultiplierValues(milestoneRow)) {
      return { mult1: milestoneRow.mult1, mult2: milestoneRow.mult2, mult3: milestoneRow.mult3 };
    }
    
    // Find contract row in Table 1 (fallback - should always have values)
    const contractRow = this.gridMultipliers.find(r => r.isContractHeader);
    
    if (contractRow) {
      return { mult1: contractRow.mult1, mult2: contractRow.mult2, mult3: contractRow.mult3 };
    }
    
    // Safety fallback (should never reach here)
    return { mult1: null, mult2: null, mult3: null };
  }

  /**
   * Transforms flat multiplier data into hierarchical structure for Table 1
   * Creates header rows for Contract and Milestones, followed by task rows
   */
  private transformToHierarchicalStructure(multipliers: RealCostMultiplier[]): any[] {
    console.log("Input multipliers:", multipliers);
    const hierarchicalData: any[] = [];
    
    if (multipliers.length === 0) {
      return hierarchicalData;
    }

    // Find contract-level multiplier (subprojectCode === null and tsTaskId === null)
    const contractMultiplier = multipliers.find(m => m.subprojectCode === null && m.tsTaskId === null);
    const contractName = contractMultiplier?.contract || multipliers[0].contract;
    const contractId = contractMultiplier?.contractId || multipliers[0].contractId;

    // Add contract header row with actual multiplier values
    hierarchicalData.push({
      contract: contractName,
      milestone: "",
      task: "",
      mult1: contractMultiplier?.mult1 ?? null,
      mult2: contractMultiplier?.mult2 ?? null,
      mult3: contractMultiplier?.mult3 ?? null,
      isContractHeader: true,
      _contractId: contractId,
      _subprojectCode: null,
      _tsTaskId: null
    });

    // Group milestone-level and task-level data
    const milestoneMap = new Map<string, { milestoneMultiplier?: RealCostMultiplier, tasks: RealCostMultiplier[] }>();
    
    multipliers.forEach(item => {
      // Skip contract-level multiplier (already added)
      if (item.subprojectCode === null && item.tsTaskId === null) {
        return;
      }
      
      if (item.subprojectCode === null) {
        return;
      }
      
      if (!milestoneMap.has(item.subprojectCode)) {
        milestoneMap.set(item.subprojectCode, { tasks: [] });
      }
      
      const milestoneData = milestoneMap.get(item.subprojectCode)!;
      
      // Milestone-level multiplier (subprojectCode set, tsTaskId null)
      if (item.tsTaskId === null) {
        milestoneData.milestoneMultiplier = item;
      } else {
        // Task-level multiplier
        milestoneData.tasks.push(item);
      }
    });

    // Build milestone and task rows
    milestoneMap.forEach((milestoneData, subprojectCode) => {
      const milestoneName = milestoneData.milestoneMultiplier?.milestone
        || milestoneData.tasks[0]?.milestone
        || '';

      // Add milestone header row with actual multiplier values
      hierarchicalData.push({
        contract: "",
        milestone: milestoneName,
        task: "",
        mult1: milestoneData.milestoneMultiplier?.mult1 ?? null,
        mult2: milestoneData.milestoneMultiplier?.mult2 ?? null,
        mult3: milestoneData.milestoneMultiplier?.mult3 ?? null,
        isMilestoneHeader: true,
        _contractId: contractId,
        _subprojectCode: subprojectCode,
        _tsTaskId: null
      });

      // Add task rows — use ONLY the task's own stored mult values, never inherit from parent
      milestoneData.tasks.forEach(task => {
        hierarchicalData.push({
          contract: "",
          milestone: "",
          task: task.task,
          mult1: task.mult1 ?? null,
          mult2: task.mult2 ?? null,
          mult3: task.mult3 ?? null,
          isTaskRow: true,
          _milestoneName: milestoneName,
          _contractId: contractId,
          _subprojectCode: subprojectCode,
          _tsTaskId: task.tsTaskId
        });
      });
    });

    console.log("Hierarchical Data:", hierarchicalData);
    return hierarchicalData;
  }
  
}
